import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CAlert } from '@coreui/react';
import { EditorContent, useEditor, useEditorState, type Editor, type Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import DOMPurify from 'dompurify';
import { AttachmentFile, AttachmentImage } from './richtext/MediaNodes';
import { MediaContext, isDisplayableImage, uploadForText } from './richtext/media';
import './rich-text.css';

/**
 * Text s formátováním — poznámky a popis zakázky. Ukládá se jako HTML; starší texty jsou
 * čistý text a zobrazují se tak, jak byly napsané.
 * <p>
 * Formátování je schválně jen pár věcí: tučně, nadpis, odrážky, číslování, odkaz.
 * <b>Kurzíva není</b> — Manrope ji nemá a brand manuál ji zakazuje; prohlížeč by písmo
 * jen uměle naklonil a vypadalo to lacině (zpětná vazba 15. 10. 2026).
 * <p>
 * Do textu jde vložit obrázek nebo soubor (zpětná vazba 15. 10. 2026) — viz
 * <c>richtext/MediaNodes.tsx</c>. Jsou to přílohy zakázky; text drží jen jejich id.
 */
const isHtml = (text: string) => /^\s*<(p|ul|ol|h[1-6]|img|div)[\s>]/i.test(text);

/** Stejné uzly pro editor i zobrazení — text pak vypadá po uložení stejně jako při psaní. */
const baseExtensions = (): Extensions => [
  StarterKit.configure({
    heading: { levels: [3] },
    italic: false,
    underline: false,
    strike: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    // Prazdny odstavec na konci by se ukladal ke kazdemu textu.
    trailingNode: false,
    // autolink by udelal odkaz "lepivy" - vsechno napsane za nim by se stalo jeho
    // soucasti. Vlozena adresa se na odkaz zmeni i bez nej (linkOnPaste).
    link: { openOnClick: false, autolink: false, linkOnPaste: true, defaultProtocol: 'https' },
  }),
  AttachmentImage,
  AttachmentFile,
];

const escape = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Odrazky z PDF a Wordu prijdou jako znaky na zacatku radku (■ • ▪ – …), ne jako seznam.
const bullet = /^\s*[■□▪▫●○•◦‣∙·\-–—*]\s+/;

/** Čistý text → HTML: řádky jako odstavce, řádky s odrážkou jako seznam. */
export function plainToHtml(text: string): string {
  const parts: string[] = [];
  let list: string[] = [];

  const flush = () => {
    if (list.length > 0) parts.push(`<ul>${list.map((i) => `<li><p>${i}</p></li>`).join('')}</ul>`);
    list = [];
  };

  for (const line of text.split(/\r?\n/)) {
    if (bullet.test(line)) {
      list.push(escape(line.replace(bullet, '').trim()));
    } else {
      flush();
      if (line.trim()) parts.push(`<p>${escape(line)}</p>`);
    }
  }

  flush();
  return parts.join('');
}

/** Pro editor: HTML zůstane, čistý text (starší záznam) se převede. */
export const toEditorHtml = (text: string) => (isHtml(text) ? text : plainToHtml(text));

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Zobrazení. HTML jde vždy přes DOMPurify — text může upravit kdokoli z týmu. Obrázky
 * nemají <c>src</c> (ten se zahodí); načtou se podle id přílohy z <paramref name="contractId"/>.
 */
export function RichTextView({ text, className, contractId }: { text: string; className?: string; contractId?: number }) {
  if (!isHtml(text)) return <div className={className} style={{ whiteSpace: 'pre-wrap' }}>{text.trim()}</div>;
  return <HtmlView html={sanitize(text)} className={className} contractId={contractId} />;
}

const sanitize = (html: string) => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'ul', 'ol', 'li', 'h3', 'a', 'img', 'div'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'alt', 'data-attachment-id', 'data-file-id', 'data-name', 'data-type', 'data-size'],
  ALLOW_DATA_ATTR: false,
});

/** Editor jen pro čtení — obrázky a kartičky souborů vykreslí stejně jako při psaní. */
function HtmlView({ html, className, contractId }: { html: string; className?: string; contractId?: number }) {
  const editor = useEditor({
    extensions: baseExtensions(),
    content: html,
    editable: false,
    editorProps: { attributes: { class: `rich-text ${className ?? ''}` } },
  });

  // Upravena poznamka nebo popis - prekreslit.
  useEffect(() => {
    if (editor && editor.getHTML() !== html) editor.commands.setContent(html, { emitUpdate: false });
  }, [editor, html]);

  return (
    <MediaContext.Provider value={contractId ?? null}>
      <EditorContent editor={editor} />
    </MediaContext.Provider>
  );
}

/** Prázdné odrážky a prázdné odstavce na konci (Enter navíc) se neukládají. */
const clean = (html: string) => html
  .replace(/<li><p><\/p><\/li>/g, '')
  .replace(/<(ul|ol)[^>]*><\/\1>/g, '')
  .replace(/(<p><\/p>)+$/, '');

/**
 * Editor. `onChange` dostane HTML, nebo prázdný řetězec, když v editoru nic není —
 * prázdný editor jinak vrací „&lt;p&gt;&lt;/p&gt;“ a to by se tvářilo jako vyplněný text.
 */
export function RichTextEditor({ value, onChange, placeholder, autoFocus, minHeight, contractId, onFilesAdded }: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Výška prázdného editoru, třeba „4rem“. */
  minHeight?: string;
  /** Zakázka, ke které se vložené soubory nahrají. Bez ní (nová zakázka) vkládat nejde. */
  contractId?: number;
  /** Nahrál se soubor — přílohy zakázky se mají obnovit. */
  onFilesAdded?: () => void;
}) {
  const editorRef = useRef<Editor | null>(null);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Nahraje soubory jako prilohy zakazky a vlozi je na misto kurzoru (nebo kam se pustily).
  async function insertFiles(files: File[], at?: number) {
    const editorNow = editorRef.current;
    if (!contractId || !editorNow || files.length === 0) return;

    setUploadError(null);
    setUploading((n) => n + files.length);
    const failed: string[] = [];
    let position = at;

    for (const file of files) {
      try {
        const attachment = await uploadForText(contractId, file);
        const node = isDisplayableImage(attachment.contentType)
          ? { type: 'attachmentImage', attrs: { id: attachment.id, name: attachment.fileName, type: attachment.contentType } }
          : { type: 'attachmentFile', attrs: { id: attachment.id, name: attachment.fileName, type: attachment.contentType, size: attachment.sizeBytes } };

        // Vzdy ZA vyber, nikdy misto nej: po vlozeni zustane obrazek oznaceny a dalsi
        // soubor by ho jinak prepsal (stalo se pri vyberu vic souboru najednou).
        const at2 = position ?? editorNow.state.selection.to;
        editorNow.chain().focus().insertContentAt(at2, node).run();
        position = undefined;
      } catch (e) {
        failed.push((e as Error).message);
      } finally {
        setUploading((n) => n - 1);
      }
    }

    if (failed.length > 0) setUploadError(failed.join(' · '));
    onFilesAdded?.();
  }

  // editorProps se nastavi jen jednou - aktualni funkci si drzi v refu (aktualizuje se po vykresleni).
  const insertRef = useRef(insertFiles);
  useLayoutEffect(() => { insertRef.current = insertFiles; });

  const editor = useEditor({
    extensions: [...baseExtensions(), Placeholder.configure({ placeholder: placeholder ?? '' })],
    content: toEditorHtml(value),
    autofocus: autoFocus ? 'end' : false,
    onCreate: ({ editor: e }) => { editorRef.current = e; },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : clean(e.getHTML())),
    editorProps: {
      attributes: { class: 'rich-text rich-editor-content', style: minHeight ? `min-height:${minHeight}` : '' },
      // Text z PDF s odrazkami (■ …) se vlozi jako skutecny seznam.
      handlePaste: (_view, event) => {
        // Screenshot nebo zkopirovany soubor -> priloha vlozena do textu.
        const files = [...(event.clipboardData?.files ?? [])];
        if (files.length > 0 && contractId) {
          void insertRef.current(files);
          return true;
        }

        const text = event.clipboardData?.getData('text/plain') ?? '';
        const bullets = text.split(/\r?\n/).filter((l) => bullet.test(l)).length;
        if (bullets < 2 || !editorRef.current) return false;

        editorRef.current.commands.insertContent(plainToHtml(text));
        return true;
      },
      // Pretazeny soubor z pocitace - vlozi se tam, kam ho clovek pustil.
      handleDrop: (view, event, _slice, moved) => {
        const files = [...(event.dataTransfer?.files ?? [])];
        if (moved || files.length === 0 || !contractId) return false;

        event.preventDefault();
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        void insertRef.current(files, at);
        return true;
      },
    },
  });

  return (
    <MediaContext.Provider value={contractId ?? null}>
      <div className="rich-editor">
        <Toolbar editor={editor} onFiles={contractId ? (files) => void insertFiles(files) : undefined} uploading={uploading > 0} />
        <EditorContent editor={editor} />
      </div>
      {uploadError && <CAlert color="danger" className="mt-2 mb-0 py-2 small">{uploadError}</CAlert>}
    </MediaContext.Provider>
  );
}

/** Telefon nebo tablet — tam má smysl tlačítko „Vyfotit“. */
const isTouchDevice = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

function Toolbar({ editor, onFiles, uploading }: { editor: Editor; onFiles?: (files: File[]) => void; uploading?: boolean }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  function pick(input: HTMLInputElement | null) {
    if (!input?.files || !onFiles) return;
    onFiles([...input.files]);
    input.value = '';
  }

  // Prekresli tlacitka pri pohybu kurzoru - aktivni formatovani je videt.
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      heading: e.isActive('heading', { level: 3 }),
      link: e.isActive('link'),
      href: (e.getAttributes('link').href as string | undefined) ?? '',
      hasSelection: !e.state.selection.empty,
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const [linking, setLinking] = useState(false);
  const chain = () => editor.chain().focus();

  return (
    <div className="rich-editor-toolbar" role="toolbar" aria-label="Formátování">
      {/* Mimo prepinanou cast listy - jinak by vstupy pri zadavani odkazu zmizely. */}
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => pick(e.currentTarget)} />
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.currentTarget)} />
      {linking ? (
        <LinkInput
          initial={state.href}
          wrapsText={state.hasSelection || state.link}
          onSave={(href, typed) => {
            setLinking(false);
            if (!href) {
              chain().extendMarkRange('link').unsetLink().run();
            } else if (state.hasSelection || state.link) {
              chain().extendMarkRange('link').setLink({ href }).run();
            } else {
              // Nic neoznaceno: vlozi se sama adresa jako odkaz. Driv se nestalo nic.
              chain().insertContent({ type: 'text', text: typed, marks: [{ type: 'link', attrs: { href } }] }).run();
            }
          }}
          onCancel={() => { setLinking(false); editor.commands.focus(); }}
        />
      ) : (
        <>
          <ToolButton label="Tučně" shortcut="Ctrl+B" active={state.bold} onClick={() => chain().toggleBold().run()}>
            <Icon d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" bold />
          </ToolButton>
          <ToolButton label="Nadpis" active={state.heading} onClick={() => chain().toggleHeading({ level: 3 }).run()}>
            <Icon d="M6 5v14M16 5v14M6 12h10" />
          </ToolButton>
          <span className="rich-editor-sep" />
          <ToolButton label="Odrážky" shortcut="začněte řádek „- “" active={state.bulletList} onClick={() => chain().toggleBulletList().run()}>
            <Icon d="M9 6h11M9 12h11M9 18h11" dots />
          </ToolButton>
          <ToolButton label="Číslovaný seznam" shortcut="začněte řádek „1. “" active={state.orderedList} onClick={() => chain().toggleOrderedList().run()}>
            <Icon d="M10 6h10M10 12h10M10 18h10M4 5h1.5v4M4 9h3M4 14.5c0-.8.6-1.5 1.5-1.5s1.5.6 1.5 1.3c0 1.2-3 1.7-3 3.7h3" />
          </ToolButton>
          <span className="rich-editor-sep" />
          <ToolButton label={state.link ? 'Upravit odkaz' : 'Odkaz'} shortcut="vyberte text" active={state.link} onClick={() => setLinking(true)}>
            <Icon d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
          </ToolButton>
          {onFiles && (
            <>
              <ToolButton label={uploading ? 'Nahrávám…' : 'Vložit obrázek nebo soubor'} shortcut="nebo vložte ze schránky / přetáhněte"
                disabled={uploading} onClick={() => fileInput.current?.click()}>
                <Icon d="M21 12.5 12.6 20.9a5.5 5.5 0 0 1-7.8-7.8l8.5-8.5a3.7 3.7 0 0 1 5.2 5.2l-8.5 8.5a1.8 1.8 0 0 1-2.6-2.6l7.8-7.8" />
              </ToolButton>
              {isTouchDevice && (
                <ToolButton label="Vyfotit" disabled={uploading} onClick={() => cameraInput.current?.click()}>
                  <Icon d="M4 8h3l2-3h6l2 3h3v11H4zM12 17a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                </ToolButton>
              )}
            </>
          )}
          <span className="ms-auto" />
          <ToolButton label="Zpět" shortcut="Ctrl+Z" disabled={!state.canUndo} onClick={() => chain().undo().run()}>
            <Icon d="M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" />
          </ToolButton>
          <ToolButton label="Znovu" shortcut="Ctrl+Y" disabled={!state.canRedo} onClick={() => chain().redo().run()}>
            <Icon d="m15 14 5-5-5-5M20 9H10a6 6 0 0 0 0 12h3" />
          </ToolButton>
        </>
      )}
    </div>
  );
}

/**
 * Adresa odkazu přímo v liště — okno prohlížeče (`prompt`) vypadalo jako chybová hláška.
 * S označeným textem z něj udělá odkaz; bez označení vloží adresu jako klikací text.
 */
function LinkInput({ initial, wrapsText, onSave, onCancel }: {
  initial: string; wrapsText: boolean; onSave: (href: string, typed: string) => void; onCancel: () => void;
}) {
  const [url, setUrl] = useState(initial);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  function save() {
    const trimmed = url.trim();
    // "statotest.com" bez https:// by byl odkaz relativne na nasi aplikaci.
    onSave(!trimmed || /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`, trimmed);
  }

  return (
    <div className="d-flex align-items-center gap-2 w-100 px-1">
      <input
        ref={input}
        className="rich-editor-link-input"
        value={url}
        placeholder={wrapsText ? 'Kam má označený text vést, např. statotest.com' : 'Adresa, vloží se jako odkaz, např. statotest.com'}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); save(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
      />
      <button type="button" className="rich-editor-text-btn rich-editor-text-btn-primary" onClick={save}>Uložit</button>
      {initial && <button type="button" className="rich-editor-text-btn" onClick={() => onSave('', '')}>Zrušit odkaz</button>}
      <button type="button" className="rich-editor-text-btn" onClick={onCancel}>Zpět</button>
    </div>
  );
}

function ToolButton({ label, shortcut, active, disabled, onClick, children }: {
  label: string; shortcut?: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`rich-editor-btn${active ? ' is-active' : ''}`}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // Bez tohohle by klik vzal editoru vyber a formatovani by se nemelo na co aplikovat.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Tenké čárové ikony, stejný styl jako zbytek rozhraní. */
function Icon({ d, bold, dots }: { d: string; bold?: boolean; dots?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={bold ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
      {dots && <><circle cx="4.5" cy="6" r="1" fill="currentColor" /><circle cx="4.5" cy="12" r="1" fill="currentColor" /><circle cx="4.5" cy="18" r="1" fill="currentColor" /></>}
    </svg>
  );
}
